import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FIRST_PAGE } from '@/api/pagination';
import { parseNumberFilter } from '@/utils/filters';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { LabeledPillList } from '@/components/common/LabeledPill';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import {
  monitorDetailInfiniteQueryOptions,
  monitorInfiniteQueryOptions,
  questionProjectsQueryOptions,
} from '../api/question.api';
import { MonitorFilterPanel } from '../components/MonitorFilterPanel';
import { CollectionProjectsTable } from '../components/CollectionProjectsTable';
import { SelectionDetailCards } from '../components/SelectionDetailCards';
import {
  getClassesLink,
  getDistrictsLink,
  getInstitutePapersLink,
  getInstitutesLink,
} from '../components/monitorLevelLinks';
import {
  getMonitorColumns,
  getMonitorDetailColumns,
  type MonitorDrillLinks,
} from '../components/QuestionColumns';
import {
  GROUPING_COLUMN_HEADER,
  GROUPING_LABEL,
  MONITOR_SCREEN_PATH,
  MONITOR_SCREENS,
  PROJECT_LOOKUP_PAGE_SIZE,
  type MonitorScreen,
  type MonitorTabId,
} from '../constants/question.constants';
import { useMonitorFilters, type MonitorRouteId } from '../hooks/useMonitorFilters';
import type { MonitorRow } from '../types/question.types';
import { getProjectPillFields } from '../utils/projectPills';

interface QuestionMonitorPageProps {
  routeId: MonitorRouteId;
  screen: MonitorScreen;
  title: string;
}

export function QuestionMonitorPage({ routeId, screen, title }: QuestionMonitorPageProps) {
  const { groupBy, requiresProject } = MONITOR_SCREENS[screen];
  const controller = useMonitorFilters(routeId);
  const { params } = controller;
  const projects = useQuery(
    questionProjectsQueryOptions({ page: FIRST_PAGE, pageSize: PROJECT_LOOKUP_PAGE_SIZE }),
  );
  const selectedProject = projects.data?.data.find(
    (project) => project.projectId === params.projectId,
  );
  const projectIdInFilters = parseNumberFilter(controller.filterValues.projectId);
  const projectInFilters = projects.data?.data.find(
    (project) => project.projectId === projectIdInFilters,
  );
  const hasProject = params.projectId !== undefined;
  const isBlocked = requiresProject && !hasProject;

  const summary = useInfiniteTableQuery(monitorInfiniteQueryOptions(params, groupBy));
  const detail = useInfiniteTableQuery(monitorDetailInfiniteQueryOptions(params));

  const [isFilterOpen, setIsFilterOpen] = useState(requiresProject && !hasProject);
  const isMemberScreen = screen === 'member';
  const lastViewedGroupId = useRevealLastViewedRow(
    isMemberScreen ? 'questionMonitorMembers' : 'questionMonitorBoards',
  );
  const lastViewedPapersId = useRevealLastViewedRow('questionInstitutePapers');

  const groupLabel = GROUPING_LABEL[groupBy];

  const drill = useMemo<MonitorDrillLinks>(() => {
    const from = MONITOR_SCREEN_PATH[screen];
    const toGroupScope = (groupId: number) =>
      isMemberScreen ? { userId: groupId } : { boardId: groupId };

    const getGroupLink = (row: MonitorRow) => {
      if (row.groupId === null) return null;
      if (isMemberScreen) {
        return getInstitutesLink({ ...params, userId: row.groupId, variant: 'byUser', from });
      }
      const breakdownSearch = { ...params, boardId: row.groupId, from };
      return screen === 'byClass'
        ? getClassesLink(breakdownSearch)
        : getDistrictsLink(breakdownSearch);
    };

    const getNewLink = (row: MonitorRow) =>
      getInstitutesLink({
        ...params,
        ...(row.groupId === null ? {} : toGroupScope(row.groupId)),
        isNew: true,
        variant: isMemberScreen ? 'byUser' : 'byAreaNew',
        from,
      });

    return { getGroupLink, getNewLink };
  }, [isMemberScreen, params, screen]);

  const summaryColumns = useMemo(
    () => getMonitorColumns(screen, GROUPING_COLUMN_HEADER[groupBy], drill),
    [screen, groupBy, drill],
  );
  const detailColumns = useMemo(
    () =>
      getMonitorDetailColumns((row) =>
        getInstitutePapersLink(row.projectInstituteId, {
          from: MONITOR_SCREEN_PATH[screen],
          projectId: row.projectId ?? undefined,
          userId: row.userId ?? undefined,
          instituteId: row.instituteId ?? undefined,
        }),
      ),
    [screen],
  );

  const tabs: TableTab<MonitorTabId>[] = [
    { id: 'summary', label: `${groupLabel} summary`, count: summary.firstPageMeta?.total },
    { id: 'institutes', label: 'Institute by institute', count: detail.firstPageMeta?.total },
  ];

  function renderSummaryTable() {
    if (summary.loadError) {
      return (
        <ErrorState
          error={summary.loadError}
          fallbackTitle="Could not load the monitor"
          onRetry={() => void summary.refetch()}
          fill
        />
      );
    }
    return (
      <DataTable
        data={summary.rows}
        columns={summaryColumns}
        isLoading={summary.isLoading}
        className="lg:flex-1"
        stickyColumnId="groupName"
        emptyMessage="Nothing is assigned here"
        isRowHighlighted={(row) => row.groupId === lastViewedGroupId}
        {...summary.loadMoreProps}
      />
    );
  }

  function renderInstituteTable() {
    if (detail.loadError) {
      return (
        <ErrorState
          error={detail.loadError}
          fallbackTitle="Could not load the institutes"
          onRetry={() => void detail.refetch()}
          fill
        />
      );
    }
    return (
      <DataTable
        data={detail.rows}
        columns={detailColumns}
        isLoading={detail.isLoading}
        className="lg:flex-1"
        stickyColumnId="instituteName"
        emptyMessage="No institute is assigned here"
        isRowHighlighted={(row) => row.projectInstituteId === lastViewedPapersId}
        {...detail.loadMoreProps}
      />
    );
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <MonitorFilterPanel filters={controller} classId={projectInFilters?.classId ?? undefined} />
      </FilterPanelCollapse>

      <SelectionDetailCards
        memberId={params.userId}
        instituteId={params.instituteId}
        boardId={params.boardId}
      />

      {selectedProject === undefined ? null : (
        <LabeledPillList fields={getProjectPillFields(selectedProject)} />
      )}

      {isBlocked ? (
        <CollectionProjectsTable
          projects={projects.data?.data ?? []}
          isLoading={projects.isLoading}
          onSelect={(projectId) => {
            void controller.navigateToProject(projectId);
          }}
        />
      ) : (
        <>
          <TableTabs
            tabs={tabs}
            activeTabId={controller.activeTabId}
            onTabChange={(tab) => void controller.changeTab(tab)}
            ariaLabel="Monitor tables"
          />
          {controller.activeTabId === 'summary' ? renderSummaryTable() : renderInstituteTable()}
        </>
      )}
    </div>
  );
}
